import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MaterialTable, { MTableBodyRow } from 'material-table';
import Paper from '@material-ui/core/Paper';
import TablePagination from '@material-ui/core/TablePagination';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import ExplicitIcon from '@material-ui/icons/Explicit';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/core/styles';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { orderActions } from 'store/modules/order';
import tableIcons from 'common/tableIcons';
import TablePaginationActions from 'components/shared/TablePaginationActions';
import TableLoadingIndicator from 'components/shared/TableLoadingIndicator';
import { fileExportFormat } from 'common/constants';
import TableDetail from './TableDetail';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(9),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(8),
    },
    '& .MuiTableFooter-root .MuiTablePagination-root': {
      display: 'block',
      boxSizing: 'border-box',
      clear: 'both',
    },
  },
  paginationToolbar: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
    },
  },
  paginationSpacer: {
    [theme.breakpoints.down('sm')]: {
      flex: '0 1 auto',
    },
  },
}));

const OrdersTable = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const {
    orders,
    pagination,
    paginatorInfo,
    loading,
  } = useSelector((state) => ({
    orders: state.order.orders,
    pagination: state.order.pagination,
    paginatorInfo: state.order.paginatorInfo,
    loading: state.order.loading.list,
  }));

  const data = orders.map((order) => ({
    ...order,
    author_id: order.author_id.full_name,
    construction_id: order.construction_id.name,
    supplier_id: order.supplier_id.name,
    delivery_date: format(parseISO(order.delivery_date), 'dd/MM/yyyy'),
    created_at: format(parseISO(order.created_at), 'dd/MM/yyyy'),
  }));

  const getDisplayedRows = () => {
    const firstItem = pagination.page === 1
      ? 1
      : (pagination.page - 1) * pagination.first;

    const lastItem = pagination.page === paginatorInfo.lastPage
      ? paginatorInfo.total
      : pagination.page * pagination.first;

    return `${firstItem}-${lastItem} de ${paginatorInfo.total}`;
  };

  const onDownloadPdf = (event, rowData) => {
    dispatch(orderActions.downloadFile({
      id: rowData.id,
      format: fileExportFormat.pdf,
    }));
  };

  const onDownloadSpreadsheet = (event, rowData) => {
    dispatch(orderActions.downloadFile({
      id: rowData.id,
      format: fileExportFormat.spreadsheet,
    }));
  };

  const onDelete = (event, rowData) => {
    const ids = rowData.map((row) => row.id);
    dispatch(orderActions.deleteOrders(ids));
  };

  const onPageChange = (event, newPage) => {
    dispatch(orderActions.updatePagination({ page: newPage }));
  };

  return (
    <Paper square className={classes.container}>
      <MaterialTable
        columns={[
          { title: 'Consec.', field: 'id' },
          { title: 'Autor', field: 'author_id' },
          { title: 'Obra', field: 'construction_id' },
          { title: 'Proveedor', field: 'supplier_id' },
          { title: 'Fecha de Entrega', field: 'delivery_date' },
          { title: 'Fecha de Creación', field: 'created_at' },
        ]}
        data={data}
        icons={tableIcons}
        isLoading={loading}
        detailPanel={[
          (rowData) => ({
            disabled: rowData.material_transactions.length === 0,
            render: () => <TableDetail data={rowData} />,
          }),
        ]}
        actions={[
          {
            name: 'downloadPdf',
            icon: () => <PictureAsPdfIcon />,
            position: 'row',
            tooltip: 'Descargar PDF',
            onClick: onDownloadPdf,
          },
          {
            name: 'downloadSpreadsheet',
            icon: () => <ExplicitIcon />,
            position: 'row',
            tooltip: 'Descargar Hoja de Cálculo',
            onClick: onDownloadSpreadsheet,
          },
          {
            icon: () => <DeleteIcon />,
            position: 'toolbarOnSelect',
            tooltip: 'Eliminar',
            onClick: onDelete,
          },
        ]}
        options={{
          actionsColumnIndex: -1,
          search: false,
          showTitle: false,
          emptyRowsWhenPaging: false,
          draggable: false,
          sorting: false,
          selection: true,
          pageSize: pagination.first,
          initialPage: pagination.page,
        }}
        localization={{
          header: {
            actions: '',
          },
          body: {
            emptyRowsWhenPaging: 'No hay ordenes de compra que mostrar. Intenta cambiando los filtros',
            emptyDataSourceMessage: 'No hay ordenes de compra que mostrar. Intenta cambiando los filtros',
          },
          toolbar: {
            nRowsSelected: '{0} fila(s) seleccionada(s)',
          },
        }}
        components={{
          OverlayLoading: () => (<TableLoadingIndicator />),
          Pagination: () => (
            <TablePagination
              classes={{
                toolbar: classes.paginationToolbar,
                spacer: classes.paginationSpacer,
              }}
              count={paginatorInfo.total}
              page={pagination.page}
              rowsPerPage={pagination.first}
              rowsPerPageOptions={[pagination.first]}
              labelDisplayedRows={getDisplayedRows}
              onChangePage={onPageChange}
              ActionsComponent={TablePaginationActions}
            />
          ),
          Row: (props) => {
            const propsCopy = { ...props };
            propsCopy.actions.find((a) => a.name === 'downloadPdf').hidden = !propsCopy.data.has_pdf;
            propsCopy.actions.find((a) => a.name === 'downloadSpreadsheet').hidden = !propsCopy.data.has_spreadsheet;
            return <MTableBodyRow {...propsCopy} />;
          },
        }}
      />
    </Paper>
  );
};

export default OrdersTable;
